import { ICalloutContentStyles } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPLink, IPText } from 'baseComponents';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { OwnerPicker } from 'components/Pickers/OwnerPicker';
import { canUpdateTaskField, TaskFieldTypes } from 'components/Tasks/TaskAuthHelper';
import CopyExternalId from 'components/Utils/CopyExternalId';
import CopyId from 'components/Utils/CopyId';
import CopyLink from 'components/Utils/CopyLink';
import { globalStackTokensGapSmall } from 'globalStyles';
import { Role } from 'models/auth/role';
import { Owner } from 'models/owner';
import Task from 'models/tasks/task';
import User from 'models/user';
import { Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ITaskDetailOwnerProps {
  orgTask: Task;
  task: Task;
  canUpdate: boolean;
  onUpdate: (task: Task) => void;
}

export const TaskDetailOwner = (props: ITaskDetailOwnerProps) => {
  const { t } = useTranslation(['translation', 'task']);
  const appContext = useContext(AppContext);
  const [showPicker, setShowPicker] = useState<boolean>(false);
  const isReadonly = !canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.Owner, appContext);

  const calloutStyle: Partial<ICalloutContentStyles> = {
    root: {
      padding: 20,
      maxWidth: 250,
    },
  };

  const updateOwner = (item: User | Role | undefined) => {
    if (item && item instanceof User) {
      const newItem = props.task.clone();
      Owner.applyItem(newItem, item);
      newItem.owner = item.clone();
      props.onUpdate(newItem);
      setShowPicker(false);
    }
  };

  const getOwnerName = (task: Task): string => {
    const user = appContext.globalDataCache.users.get(task.ownerId);

    if (user.id === appContext.user.id) {
      return t('task:Owner.You');
    } else {
      return user.name || t('task:Owner.System');
    }
  };

  //
  // Main render
  //
  return (
    <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
      <IPStack.Item>
        {!isReadonly && (
          <IPStack horizontal verticalAlign="center">
            <IPText variant="small">{t('task:Owner.OwnedBy')}</IPText>&nbsp;
            <IPLink id="task-ownerpicker-callout" onClick={() => setShowPicker(true)}>
              {getOwnerName(props.task)}
            </IPLink>
          </IPStack>
        )}
        {isReadonly && (
          <IPStack horizontal verticalAlign="center">
            <IPText variant="small">{t('task:Owner.OwnedBy')}</IPText>&nbsp;
            <IPText id="task-ownerpicker-callout">{getOwnerName(props.task)}</IPText>
          </IPStack>
        )}
      </IPStack.Item>
      {props.task.taskId !== -1 && (
        <Fragment>
          <IPStack.Item>
            <CopyId id={props.task.taskId}></CopyId>
          </IPStack.Item>
          <IPStack.Item>
            <CopyLink url={`/tasks/${props.task.taskId}`}></CopyLink>
          </IPStack.Item>
          {props.task.externalId && (
            <IPStack.Item>
              <CopyExternalId id={props.task.externalId}></CopyExternalId>
            </IPStack.Item>
          )}
        </Fragment>
      )}
      {showPicker && (
        <IPCallout
          hidden={!showPicker}
          styles={calloutStyle}
          setInitialFocus
          gapSpace={0}
          target={'#task-ownerpicker-callout'}
          onDismiss={() => {
            setShowPicker(false);
          }}
        >
          <OwnerPicker onSelect={updateOwner} disallowRoles={true} />
        </IPCallout>
      )}
    </IPStack>
  );
};
